import React, { FC } from 'react';
import { Color, Colors } from '../../colorsPalette';
import { T } from '../../helpers/helpers';
import styled from 'styled-components';
import { ColorPickerEmpty } from '../../svg/colorPickerEmpty';
import { Icon } from '../../icon';

export interface ColorPaletteInterface {
    hex: string;
    code: string;
    name: string;
}

const ColorPaletteContainer = styled.div`
    max-width: 306px;
`;

const EmbeddedColorsContainer = styled.div`
    min-width: 200px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    font-size: 12px;
`;

const EmbeddedColor = styled.div`
    display: grid;
    grid-template-areas: 'preview preview' 'name code';
    list-style: none;
    min-width: 200px;
    cursor: pointer;

    @media (hover) {
        &:hover {
            opacity: 0.7;
        }
    }
`;

const EmbeddedColorPreview = styled.div<{ color: string }>`
    height: 25px;
    border-radius: 8px;
    border: 1px #eee solid;
    background: ${(props) => props.color};
    grid-area: preview;
`;

const EmbeddedColorCode = styled.div`
    grid-area: code;
    text-align: right;
`;

const EmbeddedColorName = styled.div`
    grid-area: name;
`;

const ResetColor = styled.li`
    position: relative;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    border: 1px solid #de1c22;
    background: white;
    cursor: pointer;
    overflow: hidden;
    @media (hover) {
        &:hover {
            opacity: 0.7;
        }
    }

    ${Icon} {
        position: absolute;
        width: 100%;
        height: 100%;
    }
`;

const ColorsTitle = styled.p`
    font-size: 12px;
    line-height: 16px;
    margin: 0 0 4px;
`;

const PaletteColorsPicker: FC<{
    //color: string;
    colors: ColorPaletteInterface[];
    onChange: (color: string, name: string) => void;
    onReset?: () => void;
}> = ({ colors, onChange, onReset }) => {
    // If colors does not have a code, it is a simple color palette
    if (colors[0].code === '')
        return (
            <ColorPaletteContainer>
                <ColorsTitle>{T._('Colors', 'Admin')}</ColorsTitle>
                <Colors>
                    {onReset && (
                        <ResetColor onClick={onReset}>
                            <Icon>
                                <ColorPickerEmpty />
                            </Icon>
                        </ResetColor>
                    )}
                    {colors.map((color, idx) => (
                        <Color
                            key={idx + '-' + color.hex}
                            $hoverable
                            color={color.hex ?? '#000000'}
                            onClick={() => onChange(color.hex ?? '#000000', color.name)}
                        />
                    ))}
                </Colors>
            </ColorPaletteContainer>
        );

    // If colors has a code, it is a color palette with color codes and names
    return (
        <EmbeddedColorsContainer>
            {colors.map((color, index) => (
                <EmbeddedColor key={index + '-' + color.hex} onClick={() => onChange(color.hex, color.name)}>
                    <EmbeddedColorPreview color={color.hex} />
                    <EmbeddedColorCode>{color.code}</EmbeddedColorCode>
                    <EmbeddedColorName>{color.name}</EmbeddedColorName>
                </EmbeddedColor>
            ))}
        </EmbeddedColorsContainer>
    );
};

export default PaletteColorsPicker;
